import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { formatNotification, notificationLink } from "@/i18n/formatNotification";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const NotificationsBell = () => {
  const { user } = useAuth();
  const { t, statusLabel } = useT();
  const nav = useNavigate();
  const ntx = t.notifications || {};
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [listRes, countRes] = await Promise.all([
        api.get("/notifications", { params: { limit: 20 } }),
        api.get("/notifications/unread-count"),
      ]);
      setItems(listRes.data || []);
      setUnread(countRes.data?.count ?? 0);
    } catch {
      setItems([]);
      setUnread(0);
    }
  }, [user]);

  useEffect(() => {
    void load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [load]);

  if (!user) return null;

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      await load();
    } catch {
      /* ignore */
    }
  };

  const openNotification = async (n) => {
    if (!n.read_at) await markRead(n.id);
    const link = notificationLink(n, user);
    nav({ pathname: link.pathname, search: link.search || "" });
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && load()}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-white/80 hover:text-white" aria-label={ntx.title || "Notifications"}>
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#E11D48] text-[10px] font-bold text-white grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(100vw-2rem,22rem)] bg-[#0c0c0c] border-white/10 text-white max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0 text-[#D4AF37] text-[10px] uppercase tracking-widest font-mono-alt">
            {ntx.title || "Notifications"}
          </DropdownMenuLabel>
          {unread > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                void markAllRead();
              }}
              className="text-[10px] text-white/50 hover:text-[#D4AF37] inline-flex items-center gap-1"
            >
              <CheckCheck className="h-3 w-3" />
              {ntx.markAllRead || "Mark all read"}
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="bg-white/10" />
        {items.length === 0 && (
          <div className="px-3 py-4 text-sm text-white/50 text-center">{ntx.empty || "No notifications yet"}</div>
        )}
        {items.map((n) => {
          const { title, body } = formatNotification(n, t, statusLabel);
          return (
            <DropdownMenuItem
              key={n.id}
              className={`flex flex-col items-start gap-1 py-2.5 cursor-pointer focus:bg-white/10 ${!n.read_at ? "bg-[#D4AF37]/5 border-l-2 border-[#D4AF37]" : ""}`}
              onClick={() => openNotification(n)}
            >
              <span className="font-medium text-sm text-white/95">{title}</span>
              <span className="text-xs text-white/55 line-clamp-2">{body}</span>
              <span className="text-[10px] text-white/35 font-mono-alt">
                {(n.created_at || "").slice(0, 16).replace("T", " ")}
              </span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem asChild className="focus:bg-white/10">
          <Link to="/dashboard" className="text-xs text-[#D4AF37] w-full text-center py-2">
            {ntx.viewDashboard || "Open dashboard"}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsBell;
